import { FilterIcon } from '@heroicons/react/solid';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { useDetectClickOutside } from 'react-detect-click-outside';

import DownArrow from '../../svgs/DownArrow';
import useOnClickOutside from '../../../hooks/useClickOutside';

const TidalIconDropdownWrapper = styled.button`
  border: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.secondary};
  height: 42px;
  width: 100%;
  display: grid;
  grid-template-columns: 21px 1fr 12px;
  grid-gap: 0 10px;
  align-items: center;
  padding: 0 13px;
  cursor: pointer;
  position: relative;
  & > .selected-item {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: ${(props) => props.theme.colors.text[1]};
    display: flex;
    justify-content: flex-start;
  }
`;

const TidalIconDropdownMenuContainer = styled.div<{ isVisible: boolean }>`
  border: ${(props) =>
    props.isVisible
      ? `1px solid ${props.theme.colors.borderColors[0]}`
      : 'none'};
  border-radius: 10px;
  box-shadow: 0px 10px 49px rgb(0 27 31 / 12%);
  position: absolute;
  top: 50px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.secondary};
  max-height: 200px;
  overflow-y: auto;
  height: 0px;
  z-index: 1;
  padding: ${(props) => (props.isVisible ? '8px' : '0 8px')};
  transition: all 0.2s ease-in-out;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
`;

const TidalIconDropdownMenuItem = styled.div<{ isDropDownOpened: boolean }>`
  height: 30px;
  width: 100%;
  border-radius: 4px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  grid-gap: 8px;
  color: #000;
  font-size: 14px;
  img {
    height: 20px;
    width: 20px;
  }
  visibility: ${(props) => (props.isDropDownOpened ? 'visible' : 'hidden')};
  &:hover {
    background-color: ${(props) => `${props.theme.colors.accent}80`};
    color: #fff;
  }
`;

interface ITidalIconDropdown {
  label: string;
  items: any[];
  onSelect: (item: string) => void;
}

const TidalIconDropdown = ({
  label,
  items,
  onSelect,
}: ITidalIconDropdown): JSX.Element => {
  const [selectedItem, setSelectedItem] = useState(items[0]);
  const [isDropdownOpened, setIsDropDownOpened] = useState(false);
  const dropdownMenuRef = useRef<any>();

  const ref = useRef();
  useOnClickOutside(ref, () => {
    if (isDropdownOpened) handleDropDownToggle();
  });

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleDropDownToggle = async () => {
    try {
      if (!isDropdownOpened) {
        dropdownMenuRef.current.style.height = '200px';
        await sleep(100);
        setIsDropDownOpened(true);
      } else {
        dropdownMenuRef.current.style.height = '0px';
        await sleep(10);
        setIsDropDownOpened(false);
      }
    } catch (e) {}
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    handleDropDownToggle();
    onSelect(item.text);
  };

  return (
    <TidalIconDropdownWrapper
      ref={ref}
      onClick={() => {
        handleDropDownToggle();
      }}
    >
      <div>
        <FilterIcon />
      </div>
      <div className="selected-item">Filter: {selectedItem?.text}</div>
      <div>
        <DownArrow />
      </div>
      <TidalIconDropdownMenuContainer
        isVisible={isDropdownOpened}
        ref={dropdownMenuRef}
      >
        {items.map((item: any, index) => (
          <TidalIconDropdownMenuItem
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              handleItemSelect(item);
            }}
            isDropDownOpened={isDropdownOpened}
          >
            {item.image && (
              <img alt="" src={`/assets/dashboard/bots/${item.image}`} />
            )}
            <p>{item.text}</p>
          </TidalIconDropdownMenuItem>
        ))}
      </TidalIconDropdownMenuContainer>
    </TidalIconDropdownWrapper>
  );
};

export default TidalIconDropdown;
